import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { NavController } from "@ionic/angular";

import { AccountService } from "src/app/account/account.service";
import { UserHolding } from "src/app/account/account.model";

/**
 * Pages that belong to one holding's record or task, and the list a keeper lands on instead when
 * they switch holding there. First match wins. List pages are absent on purpose: they follow the
 * switch themselves. New session and metric session are absent too: they detect the switch and
 * leave on their own (`contextChanged`), and a second navigation would race theirs.
 */
const SECTION_ROOTS: ReadonlyArray<[RegExp, string]> = [
  [/^\/manage\/holdings\/\d+(\/|$)/, "/manage/holdings"],
  [/^\/manage\/movement-history\/.+/, "/manage/movement-history"],
  [/^\/manage\/flock\/moving-off(\/|$)/, "/manage/movement-history"],
  [/^\/manage\/flock\/metric-session(\/|$)/, ""],
  [/^\/manage\/flock\/.+/, "/manage/flock"],
  [/^\/manage\/medical\/treatment(\/|$)/, "/manage/medical"],
  [/^\/manage\/medical\/medical-cabinet\/.+/, "/manage/medical/medical-cabinet"],
  [/^\/manage\/medical\/suppliers\/.+/, "/manage/medical/suppliers"],
  [/^\/manage\/fields\/.+/, "/manage/fields"],
  [/^\/manage\/sessions\/new-session(\/|$)/, ""],
  [/^\/manage\/sessions\/.+/, "/manage/sessions"],
  [/^\/manage\/breeding\/.+/, "/manage/breeding"],
  [/^\/manage\/data\/animal-import(\/|$)/, "/manage/data"],
];

/**
 * Where a keeper should be after switching holding on `url`: the list of the section they were in
 * when the page shows one holding's record or a half-done task, or null to stay where they are.
 */
export function sectionRootAfterSwitch(url: string): string | null {
  const path = url.split(/[?#]/)[0];
  for (const [pattern, root] of SECTION_ROOTS) {
    if (pattern.test(path)) {
      return root || null;
    }
  }
  return null;
}

/**
 * The one way a keeper switches holding. Selecting the holding is not enough on its own: a record
 * or a form opened on the old holding keeps showing it, and Ionic keeps the old pages alive in its
 * stack. So a switch also takes the keeper to the section's list with a fresh stack.
 */
@Injectable({ providedIn: "root" })
export class HoldingSwitchService {
  private readonly account = inject(AccountService);
  private readonly router = inject(Router);
  private readonly navCtrl = inject(NavController);

  switchTo(holding: UserHolding): void {
    if (!holding || holding.holding === this.account.selectedHoldingId) {
      return;
    }
    const root = sectionRootAfterSwitch(this.router.url);
    this.account.updateSelectedHolding(holding);
    if (root) {
      void this.navCtrl.navigateRoot(root, { animated: false });
    }
  }
}
